import React, { useState, useEffect } from 'react';
import { useCalculator }    from './hooks/useCalculator';
import { useIngredientDB }  from './hooks/useIngredientDB';
import Header             from './components/Header';
import RecipeSetup        from './components/RecipeSetup';
import RecipeStats        from './components/RecipeStats';
import IngredientGrid     from './components/IngredientGrid';
import Method             from './components/Method';
import RecipeDrawer       from './components/RecipeDrawer';
import ScaleModal         from './components/ScaleModal';
import PdfExport          from './components/PdfExport';
import ShareExport        from './components/ShareExport';
import ShareImport        from './components/ShareImport';
import HowTo             from './components/HowTo';
import Tools             from './components/Tools';
import IngredientDatabase from './components/IngredientDatabase';
import RecipeImport     from './components/RecipeImport';
import BackupModal      from './components/BackupModal';
import Pricing          from './components/Pricing';
import styles             from './styles/App.module.css';

export default function App() {
  const ingDB = useIngredientDB();
  const calc   = useCalculator(ingDB);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [activeTab,    setActiveTab]    = useState('ingredients');
  const [showTop,      setShowTop]      = useState(false);
  const [scaleOpen,    setScaleOpen]    = useState(false);
  const [pdfOpen,      setPdfOpen]      = useState(false);
  const [helpOpen,     setHelpOpen]     = useState(false);
  const [toolsOpen,    setToolsOpen]    = useState(false);
  const [dbOpen,       setDbOpen]       = useState(false);
  const [importOpen,   setImportOpen]   = useState(false);
  const [backupOpen,   setBackupOpen]   = useState(false);
  const [pricingOpen,  setPricingOpen]  = useState(false);
  const [shareOpen,       setShareOpen]       = useState(false);
  const [shareImportOpen, setShareImportOpen] = useState(false);
  // Which help section to open. Mesa Robles links straight to #share-help, so
  // that hash has to land on the sharing instructions, not the first page.
  const [helpSection,  setHelpSection]  = useState(null);
  const [newRecipeConfirm, setNewRecipeConfirm] = useState(false);

  // Smart New Recipe — only warn if there's unsaved work worth losing
  const handleNewRecipe = () => {
    const hasIngredients = (calc.filledIngredients?.length || 0) > 0
                        || (calc.casingLines?.length || 0) > 0;
    const isUnsaved      = !calc.currentRecipeId;
    if (hasIngredients && isUnsaved) {
      setNewRecipeConfirm(true);
    } else {
      calc.clearInputs();
    }
  };

  // Deep link: /#share-help opens the sharing instructions. Mesa Robles has
  // this URL hardcoded, so it must keep working.
  useEffect(() => {
    const openFromHash = () => {
      const hash = window.location.hash.replace(/^#/, '');
      if (!hash) return;
      if (hash === 'share-help') {
        setHelpSection('share-help');
        setHelpOpen(true);
      }
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
  }, []);

  // Show back-to-top once user scrolls down 300px
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 300);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className={styles.app}>

      {/* ── STICKY TOP: nav header ── */}
      <Header
        recipeName={calc.recipeName}
        currentRecipeId={calc.currentRecipeId}
        calc={calc}
        onOpenDrawer={() => setDrawerOpen(true)}
        onScale={() => setScaleOpen(true)}
        onExport={() => setPdfOpen(true)}
        onShare={() => setShareOpen(true)}
        onHelp={() => setHelpOpen(true)}
        onOpenTools={() => setToolsOpen(true)}
        onOpenDB={() => setDbOpen(true)}
        onImport={() => setImportOpen(true)}
        onBackup={() => setBackupOpen(true)}
        onOpenPricing={() => setPricingOpen(true)}
        onClear={handleNewRecipe}
      />

      {/* ── NEW RECIPE CONFIRMATION MODAL ── */}
      {newRecipeConfirm && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(2,6,23,0.85)',
          backdropFilter:'blur(6px)', zIndex:400,
          display:'flex', alignItems:'center', justifyContent:'center', padding:'16px'
        }}>
          <div style={{
            width:'100%', maxWidth:'400px', background:'var(--bg-surface)',
            borderRadius:'var(--radius-xl)', border:'1px solid var(--border-default)',
            padding:'32px', display:'flex', flexDirection:'column', gap:'16px',
            boxShadow:'var(--shadow-lg)', animation:'fadeIn 150ms ease-out'
          }}>
            <div style={{fontSize:'1.8rem', textAlign:'center'}}>🗑️</div>
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:'1.05rem', fontWeight:700, color:'var(--text-primary)', marginBottom:'8px'}}>
                Start a new recipe?
              </div>
              <div style={{fontSize:'0.85rem', color:'var(--text-secondary)', lineHeight:1.6}}>
                You have unsaved ingredients.
                {calc.recipeName ? ` "${calc.recipeName}"` : ' This recipe'} has not been saved yet —
                clearing it will permanently lose all current data.
              </div>
            </div>
            <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
              {/* Primary action: save first, then clear */}
              <button
                style={{
                  padding:'10px 16px', borderRadius:'var(--radius-md)', border:'none',
                  background:'var(--accent)', color:'var(--text-inverse)',
                  fontFamily:'var(--font-body)', fontSize:'0.9rem', fontWeight:700,
                  cursor:'pointer'
                }}
                onClick={() => {
                  setNewRecipeConfirm(false);
                  // Open save modal so user can name and save first
                  // Header's save logic is self-contained, so we simulate the Save button
                  // by calling saveRecipe which will prompt for name if needed
                  calc.saveRecipe(calc.recipeName || 'Untitled Recipe', calc.currentRecipeId ? 'overwrite' : 'new');
                  setTimeout(() => calc.clearInputs(), 200);
                }}
              >
                💾 Save first, then clear
              </button>
              {/* Danger action: discard and clear */}
              <button
                style={{
                  padding:'10px 16px', borderRadius:'var(--radius-md)',
                  border:'1px solid rgba(239,68,68,0.4)', background:'rgba(239,68,68,0.08)',
                  color:'#F87171', fontFamily:'var(--font-body)',
                  fontSize:'0.9rem', fontWeight:600, cursor:'pointer'
                }}
                onClick={() => { setNewRecipeConfirm(false); calc.clearInputs(); }}
              >
                Discard and start new
              </button>
              {/* Cancel */}
              <button
                style={{
                  padding:'8px 16px', borderRadius:'var(--radius-md)',
                  border:'1px solid var(--border-default)', background:'transparent',
                  color:'var(--text-secondary)', fontFamily:'var(--font-body)',
                  fontSize:'0.85rem', cursor:'pointer'
                }}
                onClick={() => setNewRecipeConfirm(false)}
              >
                Cancel — keep working
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── RECIPE SETUP BAR (full width, below header) ── */}
      <RecipeSetup calc={calc} />

      {/* ── MAIN BODY: sidebar + content ── */}
      <div className={styles.body}>

        {/* LEFT: sticky stats panel */}
        <aside className={styles.sidebar}>
          <RecipeStats calc={calc} ingDB={ingDB} onOpenPricing={() => setPricingOpen(true)} />
        </aside>

        {/* RIGHT: tab-driven content */}
        <main className={styles.main}>

          {/* Tabs */}
          <div className={styles.tabs}>
            {[
              { id: 'ingredients', label: 'Ingredients' },
              { id: 'method',      label: 'Method' },
            ].map(tab => (
              <button
                key={tab.id}
                className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className={styles.tabContent}>
            {activeTab === 'ingredients' && (
              <IngredientGrid calc={calc} ingDB={ingDB} />
            )}
            {/* Method is always mounted so pendingSections useEffect fires on import */}
            <div style={{ display: activeTab === 'method' ? 'block' : 'none' }}>
              <Method
                sections={calc.methodSections}
                setSections={calc.setMethodSections}
                pendingSections={calc.pendingSections}
                onSectionsApplied={() => calc.setPendingSections(null)}
              />
            </div>
          </div>

        </main>
      </div>

      {/* ── BACK TO TOP ── */}
      {showTop && (
        <button
          className={styles.backToTop}
          onClick={scrollToTop}
          title="Back to top"
          aria-label="Back to top"
        >
          ↑
        </button>
      )}

      {/* ── MODALS ── */}
      {drawerOpen && (
        <RecipeDrawer
          calc={calc}
          onClose={() => setDrawerOpen(false)}
          onOpenShared={() => { setDrawerOpen(false); setShareImportOpen(true); }}
        />
      )}

      {scaleOpen && (
        <ScaleModal calc={calc} onClose={() => setScaleOpen(false)} />
      )}
      {pdfOpen && (
        <PdfExport calc={calc} onClose={() => setPdfOpen(false)} />
      )}

      {shareOpen && (
        <ShareExport calc={calc} ingDB={ingDB} onClose={() => setShareOpen(false)} />
      )}

      {shareImportOpen && (
        <ShareImport calc={calc} onClose={() => setShareImportOpen(false)} />
      )}
      {helpOpen && (
        <HowTo
          initialSection={helpSection}
          onClose={() => {
            setHelpOpen(false);
            setHelpSection(null);
            // Drop the hash so closing the page doesn't reopen it on reload.
            if (window.location.hash === '#share-help') {
              window.history.replaceState(null, '', window.location.pathname);
            }
          }}
        />
      )}
      {toolsOpen && (
        <Tools calc={calc} onClose={() => setToolsOpen(false)} />
      )}
      {dbOpen && (
        <IngredientDatabase ingDB={ingDB} onClose={() => setDbOpen(false)} />
      )}
      {importOpen && (
        <RecipeImport calc={calc} ingDB={ingDB} onClose={() => setImportOpen(false)} />
      )}
      {backupOpen && (
        <BackupModal onClose={() => setBackupOpen(false)} />
      )}
      {pricingOpen && (
        <Pricing calc={calc} ingDB={ingDB} onClose={() => setPricingOpen(false)} />
      )}
    </div>
  );
}
